/**
 * PECE Sprint 2 — Schedule + Resource Plan + Readiness UI
 *
 * Exports:
 *   - ScheduleTab: Tab 1.5 — list + create + delete tasks + baseline lock + critical-path
 *   - ResourcePlanTab: Tab 2 top — auto-suggest from BOQ + edit rows + Readiness dial
 *   - ReadinessBar: small widget for Material/Labour/Equipment %
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Calendar, Lock, Trash2, Plus, Zap, AlertTriangle,
  CheckCircle2, Package, HardHat, Truck,
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const DEPENDENCY_TYPES = ['FS', 'SS', 'FF', 'SF'];
const PROC_METHODS = ['purchase', 'warehouse_transfer', 'rental', 'internal_allocation', 'subcontract'];

const STATUS_COLOR = {
  not_started: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  delayed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-700',
};

// ─── Schedule Tab ──────────────────────────────────────────────────────────
export const ScheduleTab = ({ projectId, onChange }) => {
  const [tasks, setTasks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [criticalPath, setCriticalPath] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [tRes, cpRes] = await Promise.all([
        axiosInstance.get(`/pece/projects/${projectId}/schedule/tasks`),
        axiosInstance.get(`/pece/projects/${projectId}/schedule/critical-path`),
      ]);
      setTasks(tRes.data);
      setCriticalPath(cpRes.data.critical_path || []);
    } catch (e) {
      toast.error('فشل تحميل الجدول الزمني');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const handleDelete = async (id) => {
    if (!window.confirm('حذف هذه المهمة؟')) return;
    try {
      await axiosInstance.delete(`/pece/projects/${projectId}/schedule/tasks/${id}`);
      toast.success('تم الحذف');
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحذف');
    }
  };

  const handleBaselineLock = async () => {
    if (!window.confirm('قفل الجدول الزمني كـ Baseline؟ التعديلات اللاحقة ستُعتبر variations.')) return;
    try {
      const r = await axiosInstance.post(`/pece/projects/${projectId}/schedule/baseline-lock`);
      toast.success(`تم قفل ${r.data.tasks_count} مهمة كـ Baseline`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل القفل');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">جاري التحميل...</div>;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="bg-white rounded-xl p-4 border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold">الجدول الزمني للمشروع</h3>
          <span className="text-xs text-gray-500">
            {tasks.length} مهمة · Critical Path: {criticalPath.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="schedule-baseline-lock-btn"
            onClick={handleBaselineLock}
            disabled={tasks.length === 0}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1"
          >
            <Lock className="w-4 h-4" /> قفل Baseline
          </button>
          <button
            data-testid="schedule-add-task-btn"
            onClick={() => setShowAdd(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> مهمة جديدة
          </button>
        </div>
      </div>

      {/* Tasks table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-gray-700">Activity Code</th>
              <th className="text-left p-3 font-medium text-gray-700">Task Name</th>
              <th className="text-left p-3 font-medium text-gray-700">Zone</th>
              <th className="text-left p-3 font-medium text-gray-700">Baseline Start</th>
              <th className="text-left p-3 font-medium text-gray-700">Baseline Finish</th>
              <th className="text-center p-3 font-medium text-gray-700">Duration</th>
              <th className="text-center p-3 font-medium text-gray-700">Planned %</th>
              <th className="text-center p-3 font-medium text-gray-700">Actual %</th>
              <th className="text-center p-3 font-medium text-gray-700">Status</th>
              <th className="text-center p-3 font-medium text-gray-700">CP</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} className="border-b hover:bg-gray-50" data-testid={`schedule-row-${t.id}`}>
                <td className="p-2.5">
                  <code className="text-xs font-semibold text-indigo-700">{t.activity_code}</code>
                </td>
                <td className="p-2.5">{t.name}</td>
                <td className="p-2.5 text-xs text-gray-600">{t.zone || '—'}</td>
                <td className="p-2.5 text-xs">{t.baseline_start || '—'}</td>
                <td className="p-2.5 text-xs">{t.baseline_finish || '—'}</td>
                <td className="p-2.5 text-center text-xs">{t.duration_days} d</td>
                <td className="p-2.5 text-center text-xs">{fmt(t.planned_progress_pct)}%</td>
                <td className="p-2.5 text-center text-xs">
                  <span className={t.actual_progress_pct < t.planned_progress_pct ? 'text-amber-700 font-medium' : ''}>
                    {fmt(t.actual_progress_pct)}%
                  </span>
                </td>
                <td className="p-2.5 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_COLOR[t.status] || ''}`}>
                    {t.status}
                  </span>
                  {t.days_late > 0 && (
                    <span className="text-[10px] text-red-600 ml-1">+{t.days_late}d</span>
                  )}
                </td>
                <td className="p-2.5 text-center">
                  {t.is_critical_path && <Zap className="w-3.5 h-3.5 text-red-500 inline" title="Critical Path" />}
                </td>
                <td className="p-2.5 text-right">
                  <button
                    data-testid={`schedule-delete-${t.id}`}
                    onClick={() => handleDelete(t.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {tasks.length === 0 && (
              <tr>
                <td colSpan={11} className="p-12 text-center text-gray-500">
                  لا توجد مهام بعد. ابدأ بإضافة مهمة جديدة.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <ScheduleTaskModal
          projectId={projectId}
          tasks={tasks}
          onClose={() => setShowAdd(false)}
          onCreated={async () => { setShowAdd(false); await load(); onChange && onChange(); }}
        />
      )}
    </div>
  );
};

// ─── Schedule Task Modal ───────────────────────────────────────────────────
const ScheduleTaskModal = ({ projectId, tasks, onClose, onCreated }) => {
  const [form, setForm] = useState({
    activity_code: '', name: '', zone: '',
    baseline_start: '', baseline_finish: '',
    predecessor_task_id: '', dependency_type: 'FS', lag_days: 0,
  });

  const submit = async () => {
    if (!form.activity_code.trim() || !form.name.trim()) {
      toast.error('Activity Code والاسم مطلوبان');
      return;
    }
    try {
      await axiosInstance.post(`/pece/projects/${projectId}/schedule/tasks`, {
        ...form,
        predecessor_task_id: form.predecessor_task_id || null,
        lag_days: Number(form.lag_days || 0),
      });
      toast.success('تمت الإضافة');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">مهمة جديدة</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Activity Code *</label>
            <input data-testid="task-form-code" type="text" value={form.activity_code}
                   onChange={(e) => setForm({ ...form, activity_code: e.target.value })}
                   className="form-input w-full" placeholder="EL-001" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
            <input type="text" value={form.zone}
                   onChange={(e) => setForm({ ...form, zone: e.target.value })}
                   className="form-input w-full" placeholder="Building A" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Task Name *</label>
            <input data-testid="task-form-name" type="text" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="form-input w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Baseline Start</label>
            <input type="date" value={form.baseline_start}
                   onChange={(e) => setForm({ ...form, baseline_start: e.target.value })}
                   className="form-input w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Baseline Finish</label>
            <input type="date" value={form.baseline_finish}
                   onChange={(e) => setForm({ ...form, baseline_finish: e.target.value })}
                   className="form-input w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Predecessor</label>
            <select value={form.predecessor_task_id}
                    onChange={(e) => setForm({ ...form, predecessor_task_id: e.target.value })}
                    className="form-input w-full">
              <option value="">—</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>{t.activity_code} · {t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dependency Type</label>
            <select value={form.dependency_type}
                    onChange={(e) => setForm({ ...form, dependency_type: e.target.value })}
                    className="form-input w-full">
              {DEPENDENCY_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={submit} data-testid="task-form-submit"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md flex-1">إضافة</button>
          <button onClick={onClose} className="border px-4 py-2 rounded-md">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ─── Resource Plan Tab ─────────────────────────────────────────────────────
export const ResourcePlanTab = ({ projectId, onChange }) => {
  const [rows, setRows] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [rRes, kRes] = await Promise.all([
        axiosInstance.get(`/pece/projects/${projectId}/resource-plan`),
        axiosInstance.get(`/pece/projects/${projectId}/resource-plan/readiness`),
      ]);
      setRows(rRes.data);
      setReadiness(kRes.data);
    } catch (e) {
      toast.error('فشل تحميل خطة الموارد');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const autoSuggest = async () => {
    try {
      const r = await axiosInstance.post(`/pece/projects/${projectId}/resource-plan/auto-suggest`);
      toast.success(`أُضيف ${r.data.inserted} صف · تم تخطّي ${r.data.skipped}`);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التوليد');
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('حذف هذا الصف؟')) return;
    await axiosInstance.delete(`/pece/projects/${projectId}/resource-plan/${id}`);
    await load();
  };

  if (loading) return <div className="p-8 text-center text-gray-500">جاري التحميل...</div>;

  return (
    <div className="space-y-4">
      {/* Readiness Dashboard */}
      <ResourceReadinessDashboard data={readiness} />

      {/* Action bar */}
      <div className="bg-white rounded-xl p-4 border flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm">
          <span className="font-semibold">خطة الموارد:</span>{' '}
          <span className="text-gray-500">{rows.length} صف</span>
        </div>
        <button
          data-testid="rp-auto-suggest-btn"
          onClick={autoSuggest}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1"
        >
          <Zap className="w-4 h-4" /> توليد من BOQ
        </button>
      </div>

      {/* Resource Plan table */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-gray-700">Activity</th>
              <th className="text-left p-3 font-medium text-gray-700">Zone</th>
              <th className="text-left p-3 font-medium text-gray-700">Material</th>
              <th className="text-right p-3 font-medium text-gray-700">Qty</th>
              <th className="text-left p-3 font-medium text-gray-700">Unit</th>
              <th className="text-left p-3 font-medium text-gray-700">Labour</th>
              <th className="text-right p-3 font-medium text-gray-700">Qty</th>
              <th className="text-left p-3 font-medium text-gray-700">Equipment</th>
              <th className="text-right p-3 font-medium text-gray-700">Qty</th>
              <th className="text-left p-3 font-medium text-gray-700">Method</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`rp-row-${r.id}`}>
                <td className="p-2.5"><code className="text-xs">{r.activity_code}</code></td>
                <td className="p-2.5 text-xs">{r.zone || '—'}</td>
                <td className="p-2.5 text-xs">{r.material_name || '—'}</td>
                <td className="p-2.5 text-right">{fmt(r.material_qty)}</td>
                <td className="p-2.5 text-xs">{r.material_unit || '—'}</td>
                <td className="p-2.5 text-xs">{r.labour_role || '—'}</td>
                <td className="p-2.5 text-right">{r.labour_qty || 0}</td>
                <td className="p-2.5 text-xs">{r.equipment_type || '—'}</td>
                <td className="p-2.5 text-right">{r.equipment_qty || 0}</td>
                <td className="p-2.5">
                  <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">
                    {r.procurement_method}
                  </span>
                </td>
                <td className="p-2.5 text-right">
                  <button onClick={() => deleteRow(r.id)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={11} className="p-10 text-center text-gray-500">
                  لا توجد صفوف بعد. اضغط زر &quot;توليد من BOQ&quot; لإنشاء صفوف تلقائياً.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Resource Readiness Dashboard ──────────────────────────────────────────
export const ResourceReadinessDashboard = ({ data }) => {
  if (!data) return null;
  const cards = [
    {
      label: 'Materials',
      pct: data.materials_pct,
      icon: Package,
      sub: `${fmt(data.materials_delivered)} / ${fmt(data.materials_required)}`,
    },
    {
      label: 'Labour',
      pct: data.labour_pct,
      icon: HardHat,
      sub: `${data.labour_assigned} / ${data.labour_required}`,
    },
    {
      label: 'Equipment',
      pct: data.equipment_pct,
      icon: Truck,
      sub: `${data.equipment_allocated} / ${data.equipment_required}`,
    },
  ];

  const overallTone = data.overall_pct >= 80 ? 'emerald' : data.overall_pct >= 50 ? 'amber' : 'red';
  const Icon = data.overall_pct >= 80 ? CheckCircle2 : AlertTriangle;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="resource-readiness-dashboard">
      <div className={`bg-${overallTone}-50 border-2 border-${overallTone}-200 rounded-xl p-4`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs uppercase font-bold text-${overallTone}-700`}>Overall Readiness</span>
          <Icon className={`w-5 h-5 text-${overallTone}-600`} />
        </div>
        <div className={`text-4xl font-bold text-${overallTone}-700`} data-testid="readiness-overall-pct">
          {fmt(data.overall_pct)}%
        </div>
        <div className={`text-xs text-${overallTone}-600 mt-1`}>
          {data.overall_pct >= 80 ? 'جاهز' : data.overall_pct >= 50 ? 'تقدّم' : 'يحتاج اهتمام'}
        </div>
      </div>
      {cards.map((c, i) => (
        <div key={i} className="bg-white border rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase font-medium text-gray-600">{c.label}</span>
            <c.icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{fmt(c.pct)}%</div>
          <div className="text-xs text-gray-500 mt-1">{c.sub}</div>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${c.pct >= 80 ? 'bg-emerald-500' : c.pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${Math.min(100, c.pct)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
